import Button from './Button';
import FavouriteBrandList from './FavouriteBrandList';
import HomepageSectionContainer from './HomepageSectionContainer';
import HomepageSectionTitle from './HomepageSectionTitle';
import { PartnerDetail, YSH_GTM_EVENTS } from '../types/types';
import { useFetchSelectedPartners } from '../hooks/useFetchSelectedPartners.ts';
import LoadingIndicator from './LoadingIndicator';
import { useEffect, useState } from 'react';
import { useSaveSelectedPartnersMutation } from '../hooks/useSaveSelectedPartnersMutation';
import { useNavigate } from 'react-router-dom';
import { SEARCH_PARAMS_FILTERS } from '../constants/searchParamsFilters.ts';
import { pushToAnalytics } from '../utils/analytics.ts';
import ChevronCta from './ChevronCTA.tsx';

const FavouriteBrands = () => {
  const navigate = useNavigate();
  const {
    data: partners,
    isLoading,
    isSuccess,
    isRefetching,
    refetch: refetchSelectedPartners
  } = useFetchSelectedPartners();

  const [selectedPartners, setSelectedPartners] = useState<PartnerDetail[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [refetchingTriggered, setRefetchingTriggered] = useState<boolean>(false);

  const saveSelectedPartners = useSaveSelectedPartnersMutation(
    selectedPartners.map(partner => partner.id),
    () => {
      setIsEditing(false);
    },
    handleFailedToSaveSelectedPartners
  );

  const handleSaveSelectedPartners = () => {
    saveSelectedPartners.mutate();
  };

  function handleFailedToSaveSelectedPartners() {
    alert('Žao nam je, nismo uspeli da sačuvamo odabrane omiljene partnere');
    handleCancelEditing();
  }

  const handleCancelEditing = () => {
    setIsEditing(false);
    refetchSelectedPartners();
  };

  const handleEditBrands = () => {
    setIsEditing(true);
    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.EDIT_BRANDS,
      placement: 'favourite_brands',
      data: {}
    });
  };

  const handleSeeAllBrandOffers = () => {
    const placement = 'Homepage - Omiljeni brendovi';

    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.SELECT_COUPON_SECTION,
      placement,
      data: {
        coupon_details: {
          coupon_list_id: placement,
          coupon_list_name: placement
        }
      }
    });

    navigate(`/ponude?${SEARCH_PARAMS_FILTERS.FAVORITE}=1`);
  };

  useEffect(() => {
    if (partners) {
      setSelectedPartners(partners);
    }
  }, [partners]);

  // When we cancel editing, we want to reset the selected partners list -
  // We clear the selected partners here manually,
  // And once they're refetched, the list will be populated
  // With the original data kept in the database.
  useEffect(() => {
    if (isRefetching && !refetchingTriggered) {
      setSelectedPartners([]);
      setRefetchingTriggered(true);
    }

    if (!isRefetching && refetchingTriggered && partners) {
      setSelectedPartners(partners);
      setRefetchingTriggered(false);
    }
  }, [isRefetching, refetchingTriggered]);

  return (
    <>
      <HomepageSectionContainer className="homepage-section-container--bg-light-gray pb-4">
        <HomepageSectionTitle
          cta={
            <ChevronCta
              handleClick={() => (isEditing ? handleCancelEditing() : handleEditBrands())}
              label={!isEditing ? 'Uredi' : 'Otkaži'}
            />
          }
        >
          Omiljeni brendovi
        </HomepageSectionTitle>
        <section>
          <div className="mb-4">
            {isLoading && <LoadingIndicator />}
            {isSuccess && (
              <FavouriteBrandList
                selectedPartners={selectedPartners}
                setSelectedPartners={setSelectedPartners}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                placement="favourite_brands"
              />
            )}
          </div>

          {isEditing && (
            <Button onClick={() => handleSaveSelectedPartners()} className="text-center">
              Sačuvaj
            </Button>
          )}

          {!isEditing && (
            <Button onClick={() => handleSeeAllBrandOffers()} className="text-center">
              Pogledaj sve ponude omiljenih brendova
            </Button>
          )}
        </section>
      </HomepageSectionContainer>
    </>
  );
};

export default FavouriteBrands;
